import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  FiPackage, FiClock, FiStar, FiAlertTriangle, FiCheck, FiX,
  FiSun, FiMoon, FiTrendingUp, FiPercent
} from 'react-icons/fi';

// Get PST date string
function getPSTDateString(date = new Date()) {
  const pstDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const year = pstDate.getFullYear();
  const month = String(pstDate.getMonth() + 1).padStart(2, '0');
  const day = String(pstDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get start of week (Sunday)
function getStartOfWeek() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
}

// Get start of month
function getStartOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

// Refund rate color based on percentage
function getRefundRateColor(rate) {
  if (rate < 6) return 'text-emerald-600 bg-emerald-50';
  if (rate < 7) return 'text-amber-600 bg-amber-50';
  if (rate < 9) return 'text-orange-600 bg-orange-50';
  if (rate < 10) return 'text-red-600 bg-red-50';
  return 'text-red-800 bg-red-100'; // 10%+ extremely red
}

export default function DispatcherMetrics({ 
  orders = [], 
  completedOrders = [],
  adminUsers = [],
  showAllDispatchers = false // Only true for admin view
}) {
  const { adminInfo } = useSelector(s => s.login);
  const currentDispatcherName = adminInfo ? `${adminInfo.firstName} ${adminInfo.lastName}` : '';
  const isAdmin = adminInfo?.isAdmin || adminInfo?.role === 'admin';

  const metrics = useMemo(() => {
    const allOrders = [...orders, ...completedOrders];
    const startOfWeek = getStartOfWeek();
    const startOfMonth = getStartOfMonth();
    
    // Filter orders for current dispatcher (unless admin viewing all)
    const dispatcherOrders = showAllDispatchers 
      ? allOrders 
      : allOrders.filter(o => o.dispatcher === currentDispatcherName);
    
    // Orders placed count (has storeOrderId and storeSpend)
    const ordersPlaced = dispatcherOrders.filter(o => 
      o.storeOrderId?.trim() && parseFloat(o.storeSpend) > 0
    ).length;

    // Orders placed this week
    const ordersPlacedWeek = dispatcherOrders.filter(o => {
      if (!o.storeOrderId?.trim() || !parseFloat(o.storeSpend)) return false;
      const orderDate = new Date(o.placedAt || o.updatedAt || o.createdAt);
      return orderDate >= startOfWeek;
    }).length;

    // Orders placed MTD
    const ordersPlacedMTD = dispatcherOrders.filter(o => {
      if (!o.storeOrderId?.trim() || !parseFloat(o.storeSpend)) return false;
      const orderDate = new Date(o.placedAt || o.updatedAt || o.createdAt);
      return orderDate >= startOfMonth;
    }).length;

    // ─── Refund Rate Calculation ─────────────────────────────────────
    // Bad Refund = customer wanted substitute but dispatcher refunded anyway
    // Good Refund = customer wanted refund and got refund (doesn't count)
    let totalProductsHandled = 0;
    let badRefunds = 0;

    dispatcherOrders.forEach(order => {
      (order.products || []).forEach(product => {
        totalProductsHandled++;
        
        // Bad refund: customer chose substitute but it was refunded
        if (product.substituteOrRefund?.toLowerCase() === 'substitute' && product.refunded) {
          badRefunds++;
        }
      });
    });

    const refundRateWeek = totalProductsHandled > 0 
      ? ((badRefunds / totalProductsHandled) * 100).toFixed(1) 
      : 0;

    // ─── EOD/Morning Check Metrics ─────────────────────────────────────
    const eodReviewsDone = allOrders.filter(o => 
      o.eodReview?.reviewedBy === adminInfo?._id ||
      o.eodReview?.reviewedByName === currentDispatcherName
    ).length;

    const morningChecksDone = allOrders.filter(o => 
      o.morningCheck?.checkedBy === adminInfo?._id ||
      o.morningCheck?.checkedByName === currentDispatcherName
    ).length;

    // ─── Error Catch Rate (Gold Stars) ─────────────────────────────────
    // Errors caught = errors I reported on orders that someone ELSE reviewed at EOD
    let errorsCaught = 0;
    let errorsMade = 0;

    allOrders.forEach(order => {
      const errorsFound = order.morningCheck?.errorsFound || [];
      
      errorsFound.forEach(error => {
        // I reported this error
        if (error.reportedBy === adminInfo?._id || error.reportedByName === currentDispatcherName) {
          // Was the EOD review done by someone else?
          const eodReviewer = order.eodReview?.reviewedBy || order.eodReview?.reviewedByName;
          if (eodReviewer && eodReviewer !== adminInfo?._id && eodReviewer !== currentDispatcherName) {
            errorsCaught++; // Gold star - caught someone else's mistake
          }
        }
        
        // Someone else reported error on order I reviewed
        const iReviewed = order.eodReview?.reviewedBy === adminInfo?._id || 
                          order.eodReview?.reviewedByName === currentDispatcherName;
        const someoneElseReported = error.reportedBy !== adminInfo?._id && 
                                     error.reportedByName !== currentDispatcherName;
        
        if (iReviewed && someoneElseReported) {
          errorsMade++; // Negative mark - someone found my mistake
        }
      });
    });

    // ─── Dispatch Time Metrics ─────────────────────────────────────
    // Only calculate for orders where inStockDate was ≤ 3 days from creation
    const dispatchTimes = [];
    const placementTimes = [];

    dispatcherOrders.forEach(order => {
      if (!order.createdAt) return;
      
      const createdDate = new Date(order.createdAt);
      const inStockDate = new Date(order.inStockDate);
      const daysDiff = (inStockDate - createdDate) / (1000 * 60 * 60 * 24);
      
      // Only measure if inStockDate was ≤ 3 days away
      if (daysDiff <= 3) {
        // Dispatch total time (to requested status)
        if (order.requestedAt) {
          const requestedDate = new Date(order.requestedAt);
          const dispatchTime = (requestedDate - createdDate) / (1000 * 60 * 60); // hours
          dispatchTimes.push(dispatchTime);
        }
        
        // Grocery placement time (to when storeOrderId was set)
        if (order.placedAt && order.storeOrderId) {
          const placedDate = new Date(order.placedAt);
          const placementTime = (placedDate - createdDate) / (1000 * 60 * 60); // hours
          placementTimes.push(placementTime);
        }
      }
    });

    const avgDispatchTime = dispatchTimes.length > 0
      ? (dispatchTimes.reduce((a, b) => a + b, 0) / dispatchTimes.length).toFixed(1)
      : null;

    const avgPlacementTime = placementTimes.length > 0
      ? (placementTimes.reduce((a, b) => a + b, 0) / placementTimes.length).toFixed(1)
      : null;

    return {
      ordersPlaced,
      ordersPlacedWeek,
      ordersPlacedMTD,
      refundRateWeek: parseFloat(refundRateWeek),
      badRefunds,
      totalProductsHandled,
      eodReviewsDone,
      morningChecksDone,
      errorsCaught,
      errorsMade,
      avgDispatchTime,
      avgPlacementTime,
    };
  }, [orders, completedOrders, currentDispatcherName, adminInfo, showAllDispatchers]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {showAllDispatchers ? 'Team Performance' : 'Your Performance'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {showAllDispatchers ? 'All dispatchers' : currentDispatcherName}
          </p>
        </div>
        <FiTrendingUp className="w-6 h-6 text-gray-400" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Orders Placed */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiPackage className="w-4 h-4 text-sky-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Orders Placed</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.ordersPlaced}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Week: {metrics.ordersPlacedWeek} | MTD: {metrics.ordersPlacedMTD}
          </div>
        </div>

        {/* Refund Rate */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiPercent className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Refund Rate</span>
          </div>
          <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xl font-bold ${getRefundRateColor(metrics.refundRateWeek)}`}>
            {metrics.refundRateWeek}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {metrics.badRefunds} bad / {metrics.totalProductsHandled} total
          </div>
        </div>

        {/* EOD Reviews */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiMoon className="w-4 h-4 text-violet-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">EOD Reviews</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.eodReviewsDone}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Reviews completed</div>
        </div>

        {/* Morning Checks */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiSun className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Morning Checks</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.morningChecksDone}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Checks completed</div>
        </div>

        {/* Gold Stars (Errors Caught) */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiStar className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase">Gold Stars</span>
          </div>
          <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">{metrics.errorsCaught}</div>
          <div className="text-xs text-amber-600 dark:text-amber-500 mt-1">Errors caught on others</div>
        </div>

        {/* Negative Marks (Errors Made) */}
        <div className={`rounded-xl p-4 ${metrics.errorsMade > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FiAlertTriangle className={`w-4 h-4 ${metrics.errorsMade > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            <span className={`text-xs font-medium uppercase ${metrics.errorsMade > 0 ? 'text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              Issues Found
            </span>
          </div>
          <div className={`text-2xl font-bold ${metrics.errorsMade > 0 ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
            {metrics.errorsMade}
          </div>
          <div className={`text-xs mt-1 ${metrics.errorsMade > 0 ? 'text-red-600 dark:text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
            Errors on your reviews
          </div>
        </div>

        {/* Avg Dispatch Time */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiClock className="w-4 h-4 text-sky-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dispatch Time</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {metrics.avgDispatchTime ? `${metrics.avgDispatchTime}h` : '–'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg to requested status</div>
        </div>

        {/* Avg Placement Time */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FiCheck className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Placement Time</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {metrics.avgPlacementTime ? `${metrics.avgPlacementTime}h` : '–'}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg to order placed</div>
        </div>
      </div>

      {/* Refund Rate Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Refund Rate Thresholds:</p>
        <div className="flex items-center gap-4 text-xs">
          <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-600">&lt;6% Good</span>
          <span className="px-2 py-1 rounded bg-amber-50 text-amber-600">6% Warning</span>
          <span className="px-2 py-1 rounded bg-orange-50 text-orange-600">7-8% Concern</span>
          <span className="px-2 py-1 rounded bg-red-50 text-red-600">9-10% Critical</span>
          <span className="px-2 py-1 rounded bg-red-100 text-red-800">&gt;10% Severe</span>
        </div>
      </div>
    </div>
  );
}

